// components/NetSalesGrossProfitCashChart.jsx
import { Card, Box, Typography } from "@mui/material";
import {
    ComposedChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    Area,
    LabelList,
    ResponsiveContainer,
    CartesianGrid
} from "recharts";

// Custom hollow dot for Cash Balance dashed line
const CustomDot = ({ cx, cy, color }) => {
    if (cx === undefined || cy === undefined) return null;
    return (
        <g>
            <circle cx={cx} cy={cy} r={4} fill="#ffffff" stroke={color} strokeWidth={2} />
        </g>
    );
};

export default function LineChartsale({
    title,
    xLabels,
    series,
    dataMatrix,
    yTicks,
    fillColor = "#62BBFF",  // Default fill color, can be overridden from parent
    yAxisLabel
}) {

    // ---- Convert dataMatrix → Recharts rows ----
    const rows = xLabels.map((month, rowIndex) => {
        const row = { month };
        series.forEach((s, colIndex) => {
            row[s.key] = dataMatrix[rowIndex][colIndex];
        });
        return row;
    });

    // Y-Axis domain top = last tick
    const yTop = yTicks[yTicks.length - 1];
    const chartWidth = Math.max(rows.length * 90, 900);


    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;

        // Remove duplicates using Map
        const uniquePayload = Array.from(
            new Map(payload.map((item) => [item.dataKey, item])).values()
        );

        return (
            <div
                style={{
                    background: "#fff",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "8px"
                }}
            >
                <p style={{ fontWeight: "600" }}>{label}</p>

                {uniquePayload.map((item) => (
                    <p key={item.dataKey} style={{ margin: 0 }}>
                        {item.name}: ${item.value}M
                    </p>
                ))}
            </div>
        );
    };


    return (

        <Card
            sx={{
                width: "100%",
                borderRadius: "12px !important",
                background: "#fff",
                overflow: "hidden",
                boxShadow: "0px 1px 12px rgba(0, 0, 0, 0.1)",
                position: "relative",
                overflow: "visible",
                height: "100%"
            }}
        >

            {/* HEADER */}
            <Box
                sx={{
                    position: "absolute",
                    top: "-10px",
                    left: "12px",
                    right: "12px",
                    background: "#115f9c",
                    height: "36px",
                    borderRadius: "4px !important",
                    display: "flex",
                    alignItems: "center",
                    px: "12px",
                    color: "#fff",
                    fontSize: "14px",
                    fontWeight: 200,
                    boxShadow: "0px 1px 16px rgba(128, 203, 255, 0.5)"
                }}
            >
                {title}
            </Box>

            {/* CHART AREA */}
            <Box sx={{
                width: "100%", height: 420, p: 2, overflowX: "auto",     //  enable horizontal scroll
                overflowY: "hidden",
            }}>
                <Box
                    sx={{
                        width: {
                            xs: chartWidth,   //  enable horizontal scroll on mobile
                            sm: chartWidth,   // enable horizontal scroll on tablet
                            md: chartWidth,
                            lg: "100%"
                        },
                        minWidth: {
                            xs: chartWidth,
                            sm: chartWidth,
                            md: chartWidth,
                            lg: "100%"
                        },
                        height: "100%",
                    }}
                >

                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                            data={rows}
                            margin={{ top: 40, right: 40, left: 10, bottom: 10 }}
                        >

                            {/* GRADIENTS - Uses series colors (solid fill, no fade) */}
                            <defs>
                                {/* Net Sales fill - uses first series color or fillColor */}
                                <linearGradient id="netFillGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={series[0]?.color || fillColor} stopOpacity={0.35} />
                                    <stop offset="100%" stopColor={series[0]?.color || fillColor} stopOpacity={0.35} />
                                </linearGradient>

                                {/* Gross Profit fill - uses second series color */}
                                <linearGradient id="grossFillGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={series[1]?.color || fillColor} stopOpacity={0.35} />
                                    <stop offset="100%" stopColor={series[1]?.color || fillColor} stopOpacity={0.35} />
                                </linearGradient>

                                {/* Stock fill */}
                                <linearGradient id="stockFillGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={series[0]?.color || fillColor} stopOpacity={0.35} />
                                    <stop offset="100%" stopColor={series[0]?.color || fillColor} stopOpacity={0.35} />
                                </linearGradient>

                                {/* Sold fill */}
                                <linearGradient id="soldFillGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={series[1]?.color || fillColor} stopOpacity={0.35} />
                                    <stop offset="100%" stopColor={series[1]?.color || fillColor} stopOpacity={0.35} />
                                </linearGradient>
                            </defs>

                            {/* GRID */}
                            <CartesianGrid stroke="#1111110D" vertical={false} />

                            {/* X-AXIS */}
                            <XAxis
                                dataKey="month"
                                interval={0}
                                tick={{ fontSize: 10, color: "#111111", fontWeight: 200, }}
                                padding={{ left: 30, right: 20 }}
                                axisLine={{ stroke: "#E5E5E5" }}
                                tickLine={false}
                            />

                            {/* Y-AXIS (ONLY modification: uses yTicks) */}
                            <YAxis
                                domain={[0, yTop]}
                                ticks={yTicks}
                                tick={{ fontSize: 10, fill: "#11111180", dx: -12, fontWeight: 200 }} // move labels left
                                axisLine={false}
                                tickLine={false}
                                label={{
                                    value: yAxisLabel,
                                    angle: -90,
                                    position: "insideLeft",
                                    dx: 5,
                                    dy: 40,
                                    style: { fontSize: 10, fill: "#111111", fontWeight: 200 }
                                }}
                            />

                            <Tooltip content={<CustomTooltip />} />

                            {/* ==== AREA FILLS (Automatically pick correct gradient) ==== */}
                            {series.map((s, i) => {
                                let fillId = null;

                                // Map gradient to correct series key
                                if (s.key === "net") fillId = "netFillGradient";
                                if (s.key === "gross") fillId = "grossFillGradient";
                                if (s.key === "stock") fillId = "stockFillGradient";
                                if (s.key === "sold") fillId = "soldFillGradient";

                                // Forecast has NO fill
                                if (!fillId) return null;

                                return (
                                    <Area
                                        key={s.key + "-area"}
                                        type="linear"
                                        dataKey={s.key}
                                        stroke="none"
                                        fill={`url(#${fillId})`}
                                        isAnimationActive={false}
                                    />
                                );
                            })}


                            {/* ==== AREA FILLS ==== */}
                            <Area
                                type="linear"
                                dataKey={series[0].key}
                                stroke="none"
                                fill="url(#netFillGradient)"
                                isAnimationActive={false}
                            />

                            <Area
                                type="linear"
                                dataKey={series[1].key}
                                stroke="none"
                                fill="url(#grossFillGradient)"
                                isAnimationActive={false}
                            />
                            <Area
                                type="linear"
                                dataKey={series[0].key}
                                stroke="none"
                                fill="url(#stockFillGradient)"
                                isAnimationActive={false}
                            />

                            <Area
                                type="linear"
                                dataKey={series[1].key}
                                stroke="none"
                                fill="url(#soldFillGradient)"
                                isAnimationActive={false}
                            />


                            {/* ==== LINES ==== */}
                            {series.map((s) => (
                                <Line
                                    key={s.key}
                                    type="linear"
                                    dataKey={s.key}
                                    stroke={s.color}
                                    strokeWidth={s.dashed ? 2 : 2.5}
                                    strokeDasharray={s.dashed ? "6 4" : "0"}
                                    dot={
                                        s.dashed
                                            ? <CustomDot color={s.color} />
                                            : {
                                                r: 4,
                                                fill: s.color,
                                                stroke: s.color,
                                                strokeWidth: 2
                                            }
                                    }
                                    activeDot={{
                                        r: 6,
                                        fill: "#fff",
                                        stroke: s.color,
                                        strokeWidth: 2
                                    }}
                                    isAnimationActive={false}
                                >

                                    <LabelList
                                        dataKey={s.key}
                                        position="top"
                                        dy={-8}
                                        formatter={(v) => `$${v}M`}
                                        style={{ fontSize: 8, fill: "#11111180", fontWeight: 400 }}
                                    />


                                </Line>
                            ))}

                        </ComposedChart>
                    </ResponsiveContainer>

                </Box>
            </Box>

            {/* LEGEND */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    gap: 2,
                    m: '16px'
                }}
            >
                {series.map((s) => (
                    <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                backgroundColor: s.color
                            }}
                        />
                        <Typography fontSize={10} style={{ color: "#111111", fontWeight: 200 }}>{s.label}</Typography>
                    </Box>
                ))}
            </Box>
        </Card>
    );
}
